/**
 * Demo Banner (M10 Phase 5 · Competition Mode).
 *
 * Rendered at the top of every authenticated route when the active workspace
 * is the seeded ``demo`` workspace. Explains sandbox status and exposes a
 * one-click Reset button that rebuilds the entire workspace from seed data.
 *
 * Isolated by design: the banner never appears on real customer workspaces.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Rocket, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useCurrentWorkspace } from "@/entities/workspace";
import { Button } from "@/shared/ui/button";
import { http } from "@/shared/api/http";

interface DemoStatus {
  workspace_id: string | null;
  slug: string;
  display_name?: string;
  seeded: boolean;
  last_reset_at?: string;
  counts?: Record<string, number>;
}

async function getDemoStatus(): Promise<DemoStatus> {
  return http<DemoStatus>("/demo/status");
}

async function resetDemo(): Promise<DemoStatus> {
  return http<DemoStatus>("/demo/reset", { method: "POST" });
}

export function DemoBanner(): JSX.Element | null {
  const ws = useCurrentWorkspace();
  const [dismissed, setDismissed] = useState(false);
  const qc = useQueryClient();
  const isDemo = ws?.slug === "demo";
  const statusQ = useQuery({
    queryKey: ["demo-status"],
    queryFn: getDemoStatus,
    // Only poll (and only run at all) when we're actually in the demo
    // workspace. Prevents a background /demo/status request every 60s from
    // every authenticated page and eliminates a false "refresh" signal.
    enabled: isDemo,
    refetchInterval: isDemo ? 120_000 : false,
    refetchOnWindowFocus: false,
  });
  const reset = useMutation({
    mutationFn: resetDemo,
    onSuccess: () => {
      toast.success("Demo workspace rebuilt from seed data");
      qc.invalidateQueries();
    },
    onError: (e: Error) => toast.error(`Demo reset failed: ${e.message}`),
  });

  if (dismissed) return null;
  if (!isDemo) return null;

  const counts = statusQ.data?.counts ?? {};
  return (
    <div
      data-testid="demo-banner"
      className="sticky top-0 z-30 flex items-center gap-3 border-b border-primary/40 bg-gradient-to-r from-primary/15 via-primary/8 to-transparent px-4 py-2.5 text-sm backdrop-blur-md"
    >
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/20 text-primary">
        <Rocket className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-primary">
            Competition Mode · Demo Workspace
          </span>
          <span className="text-xs text-muted-foreground">
            Sandbox-only · nothing here touches production data.
          </span>
        </div>
        {statusQ.data ? (
          <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
            {counts.campaigns ?? 0} campaigns · {counts.assets ?? 0} assets ·{" "}
            {counts.media_assets ?? 0} media · {counts.publishing_connections ?? 0} channels connected
          </div>
        ) : null}
      </div>
      <Button
        data-testid="demo-reset-btn"
        size="sm"
        variant="outline"
        onClick={() => reset.mutate()}
        disabled={reset.isPending}
        className="gap-1"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${reset.isPending ? "animate-spin" : ""}`} />
        {reset.isPending ? "Rebuilding…" : "Reset Demo"}
      </Button>
      <button
        data-testid="demo-banner-dismiss"
        onClick={() => setDismissed(true)}
        className="rounded-full p-1 text-muted-foreground transition hover:bg-background/60 hover:text-foreground"
        aria-label="Dismiss demo banner"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
